import React, { useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  TableRow,
  TableCell,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { getSearchType } from '../subscriber/SubscriberRequests'
import { Type } from '../../store/searcher/reducer'
import { StaticDataTable } from './StaticDataTable'
import { useTableStyles } from '../datatable/hook/useStyles'

interface Request {
  type: string
  count: number
  userId: number
}
interface RequestByUser {
  count: number
  userId: number
}
interface UsageStat {
  date: string
  count: number
  users_count: number
  users: number[]
  types_present: string[]
  web_requests: number
  api_requests: number
  requests: Request[]
  requests_by_user: RequestByUser[]
  startDate: string,
}
interface Props {
  data: UsageStat[],
  monthRange: any
}

export const UsageStatsAll: React.FC<Props> = ({ data, monthRange }) => {

  const classesTable = useTableStyles()

  const allTypes = useMemo<string[]>(
    () => Array.from(new Set((data ?? []).flatMap(d => d.types_present))).sort(),
    [data],
  )

  const [typeFilter, setTypeFilter] = useState<'all' | string>('all')

  const initialExpanded = useMemo(
    () => new Set<number>((data ?? []).map((_, i) => i)),
    [data],
  )
  const [expanded, setExpanded] = useState<Set<number>>(initialExpanded)

  const isAllExpanded = expanded.size === (data?.length ?? 0)
  const toggleOne = (idx: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }
  const toggleAll = () => {
    if (!data) return
    setExpanded(prev =>
      prev.size === data.length ? new Set() : new Set(data.map((_, i) => i)),
    )
  }

  const filteredData = useMemo(
    () =>
      (data ?? []).filter(d =>
        typeFilter === 'all' ? true : d.types_present?.includes(typeFilter),
      ),
    [data, typeFilter],
  )

  const filteredTotalCount = useMemo(
    () => (filteredData ?? []).reduce((sum, d) => sum + (d?.count ?? 0), 0),
    [filteredData],
  )

  if (!data || data.length === 0) {
    return <Typography align="center">No data available</Typography>
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
      >
        <Typography variant="h6">
          Usage details for period from{" "}
          <Box component="span" sx={{ textDecoration: "underline" }}>
            {monthRange.start}
          </Box>{" "}
          to{" "}
          <Box component="span" sx={{ textDecoration: "underline" }}>
            {monthRange.end}
          </Box>
          {": "}
          <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
            {filteredTotalCount}
          </Box>
        </Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="type-filter-label">Filter by type</InputLabel>
            <Select
              labelId="type-filter-label"
              label="Filter by type"
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter((e.target.value as string) || 'all')
              }
            >
              <MenuItem value="all">
                <em>All types</em>
              </MenuItem>
              {allTypes.map((t: string) => (
                <MenuItem key={t} value={t}>{getSearchType(t as Type)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={isAllExpanded ? 'Collapse all' : 'Expand all'}>
            <IconButton onClick={toggleAll}>
              {isAllExpanded ? <ExpandLessIcon sx={{ color: 'primary.main' }} /> : <ExpandMoreIcon sx={{ color: 'primary.main' }} />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {filteredData.length === 0 && (
        <Typography align="center">No days match selected type.</Typography>
      )}

      {filteredData.map((item, idx) => {
        const className = idx % 2 !== 0 ? classesTable.rowEven : ''
        const open = expanded.has(idx)
        return (
          <Card key={`${item.date}-${idx}`} variant="outlined">
            <CardHeader
              title={`📅 ${item.date}`}
              subheader={` · Total: ${item.count}`}
              action={
                <Tooltip title={open ? 'Collapse' : 'Expand'}>
                  <IconButton onClick={() => toggleOne(idx)}>
                    {open ? <ExpandLessIcon sx={{ color: 'primary.main' }} /> : <ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                  </IconButton>
                </Tooltip>
              }
            />
            <Collapse in={open} timeout="auto" unmountOnExit>
              <CardContent>
                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={4} mb={2} flexWrap="wrap">
                  <Typography variant="body1">Total requests: <b>{item.count}</b></Typography>
                  <Typography variant="body1">Users: <b>{item.users_count}</b></Typography>
                  <Typography variant="body1">Web: <b>{item.web_requests}</b></Typography>
                  <Typography variant="body1">API: <b>{item.api_requests}</b></Typography>
                </Stack>

                <Box mb={2}>
                  <Typography variant="subtitle1">Users IDs:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {item.users.map((u) => (
                      <Chip key={u} label={u} size="small" color="info" component="a" href={`/users/subscriber/${u}`} clickable target="_blank" rel="noopener noreferrer" />
                    ))}
                  </Box>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle1">Types present:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {item.types_present.map((t) => (
                      <Tooltip key={t} title={getSearchType(t as Type) || "No type detected"}>
                        <Chip key={t} label={t} size="small" color="info" />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>

                <Box mb={2}>
                  <StaticDataTable<RequestByUser>
                    title="Requests by User"
                    rows={item.requests_by_user}
                    headerData={[
                      {
                        key: 'userId',
                        text: 'UserId',
                        defaultSort: false,
                        style: { alignItems: 'center', whiteSpace: 'nowrap' },
                      },
                      {
                        key: 'count',
                        text: 'Count',
                        defaultSort: true,
                        style: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
                      },
                    ]}
                    renderBody={(ru, i) => (
                      <TableRow key={`${ru.userId}-${i}`}>
                        <TableCell classes={classesTable} align="center">
                          <Link
                            href={`/users/subscriber/${ru.userId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            {ru.userId}
                          </Link>
                        </TableCell >
                        <TableCell classes={classesTable} align="center">{ru.count}</TableCell>
                      </TableRow>
                    )}
                    pagination
                    limit={10}
                  />
                </Box>

                <Box sx={{ '& .MuiText-root:first-of-type, & .MuiTypography-root:first-of-type': { mt: '5px' } }}>
                  <StaticDataTable<Request>
                    title="Requests by Type & User"
                    rows={item.requests}
                    headerData={[
                      { key: 'type', text: 'Type', defaultSort: false, style: { justifyContent: 'center', alignItems: 'center' } },
                      { key: 'userId', text: 'UserId', defaultSort: false, style: { justifyContent: 'center', alignItems: 'center' } },
                      { key: 'count', text: 'Count', defaultSort: true, style: { display: 'flex', justifyContent: 'center', alignItems: 'center' } },
                    ]}
                    renderBody={(r, i) => (
                      <TableRow key={`${r.type}-${r.userId}-${i}`} className={className}>
                        <TableCell classes={classesTable} align="center">
                          <Tooltip title={getSearchType(r.type as Type) || 'No type detected'}>
                            <span>{r.type}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell classes={classesTable} align="center">
                          <Link
                            href={`/users/subscriber/${r.userId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            {r.userId}
                          </Link>
                        </TableCell>
                        <TableCell classes={classesTable} align="center">{r.count}</TableCell>
                      </TableRow>
                    )}
                    pagination
                    limit={10}
                  />
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        )
      })}
    </Stack>
  )
}

export default UsageStatsAll
